'use client';

import React, {
  FC,
  useEffect,
  useRef,
  useState,
  memo,
  useCallback,
} from 'react';
import Slider, { Settings } from 'react-slick';
import {
  TypeImage,
  TypeMediaTextTitle,
  TypeVideo,
  TypeVideoEmbedFields,
} from '@/core/generated-types';
import {
  ChevronIcon,
  HeaderText,
  LuvImage,
  RichText,
  Video,
  VideoEmbed,
} from '@/components';
import { Asset } from 'contentful';
import { CAROUSEL_SLIDES_TO_SHOW } from '@/core/constants';
import { HeaderSize } from '@/components/ui/HeaderText';
import useProductContext from '@/context/ProductContext';
import usePreventSwipeBackGesture from '@/hooks/usePreventSwipeBackGesture';
import { Document } from '@contentful/rich-text-types';
import PlayIcon from '@/public/assets/icons/play.svg';
import { useMatchesMediaQuery } from '@/hooks/useMatchesMediaQuery';
type SLIDER_TYPE = Slider | undefined;

interface ThumbnailArrowProps {
  direction: 'right' | 'left';
  onClick: React.MouseEventHandler<HTMLButtonElement>;
}

const ThumbnailArrow = memo(
  ({ direction = 'right', onClick = () => null }: ThumbnailArrowProps) => {
    const style = direction === 'right' ? 'right-[-64px]' : 'left-[-42px]';

    return (
      <button
        className={`button absolute ${style} top-[28%] h-[32px] w-[32px] bg-no-repeat`}
        aria-label={direction == 'right' ? 'Next' : 'Previous'}
        onClick={onClick}
      >
        <ChevronIcon
          classes="mt-1 mr-4 pl-1 md:pl-0 min-w-[16px]"
          direction={direction}
        />
      </button>
    );
  },
);

ThumbnailArrow.displayName = 'ThumbnailArrow';

interface MediaSlideVideo extends TypeVideo {
  hasControls?: boolean;
}

const MediaSlide: FC<
  (TypeImage | MediaSlideVideo | TypeMediaTextTitle) & { isThumbnail?: boolean }
> = memo((content) => {
  const contentTypeId = content.sys?.contentType?.sys?.id;
  const { isThumbnail } = content;

  const isMobile = useMatchesMediaQuery({
    query: `(max-width: 640px)`,
  });

  switch (contentTypeId) {
    case 'image':
      const entryImage = content as TypeImage;
      return (
        <div className="flex items-center p-2" data-imageid={content.sys.id}>
          <LuvImage
            imageData={entryImage}
            alt={entryImage.fields?.altText}
            width={700}
            loading={isThumbnail ? 'lazy' : 'eager'}
          />
        </div>
      );
    case 'video':
      const entryVideo = content as MediaSlideVideo;
      const {
        fields: {
          file: { url },
        },
      } = entryVideo?.fields?.video as Asset;
      const { hasControls } = entryVideo;
      return (
        <div className="p-2">
          <Video
            src={`https:${url}`}
            autoplay={true}
            loop={true}
            muted={true}
            playsinline={isMobile}
            controls={hasControls}
          />
        </div>
      );
    case 'videoEmbed':
      const { platform, componentName, videoId, videoParams } =
        content.fields as TypeVideoEmbedFields;

      if (isThumbnail) {
        const thumbnail = (content.fields as TypeVideoEmbedFields).thumbnail
          ?.fields.file.url;
        return (
          <div className="flex items-center relative">
            {thumbnail ? (
              <span className="brightness-75">
                <img src={thumbnail} alt="Video Thumbnail" />
              </span>
            ) : (
              <div className="bg-[#D9D9D9] p-2 flex min-h-12 w-16 mx-auto" />
            )}
            <img
              src={PlayIcon.src}
              alt="Video Play icon"
              className="!h-5 !border-none brightness-0 invert absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
            />
          </div>
        );
      }
      return (
        <div className="p-2">
          <VideoEmbed
            platform={platform}
            componentName={componentName}
            videoId={videoId}
            entryName={videoId}
            videoParams={videoParams}
            classes="w-full h-[270px] md:h-[420px]"
          />
        </div>
      );
    case 'mediaTextTitle':
      const entryMedia = content as TypeMediaTextTitle;
      const { entryName, image, textHeading, textBody } = entryMedia.fields;

      if (!image) return null;

      return (
        <div
          className="flex items-center justify-center pb-12 pt-2"
          data-imageid={content.sys.id}
        >
          <div>
            <LuvImage
              entryName={entryName}
              Image={image?.fields?.Image}
              altText={textHeading}
              width={200}
              height={200}
              className="media-text-title-img"
              loading="eager"
            />
            <div className="mt-1 px-6 text-center">
              <HeaderText text={textHeading} size={HeaderSize.H3} />
              {textBody && (
                <div className="mt-1">
                  <RichText content={textBody as Document} />
                </div>
              )}
            </div>
          </div>
        </div>
      );
    default:
      console.warn(
        `Invalid content type "${contentTypeId}" for carousel slide.`,
      );
      return null;
  }
});

MediaSlide.displayName = 'MediaSlide';

interface CarouselProductsProps {
  components: (TypeImage | TypeVideo | TypeMediaTextTitle)[];
  id?: string;
  classes?: string;
}

const CarouselProducts: FC<CarouselProductsProps> = ({
  components = [],
  id,
  classes,
}) => {
  const { carouselImagePosition } = useProductContext();
  const [mainSlider, setMainSlider] = useState<Slider | null>(null);
  const [thumbnailSlider, setThumbnailSlider] = useState<Slider | null>(null);
  const mainSliderRef = useRef<Slider | null>(null);
  const thumbnailSliderRef = useRef<Slider | null>(null);

  useEffect(() => {
    setMainSlider(mainSliderRef.current);
    setThumbnailSlider(thumbnailSliderRef.current);
  }, []);

  useEffect(() => {
    if (!carouselImagePosition) return;
    // slickGoTo args (Args: index, dontAnimate)
    thumbnailSlider?.slickGoTo(carouselImagePosition, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselImagePosition]);
  const next = useCallback(() => {
    thumbnailSliderRef.current?.slickNext();
  }, []);

  const previous = useCallback(() => {
    thumbnailSliderRef.current?.slickPrev();
  }, []);

  const maxSlidesToShow = components.length - 1;
  const slidesToShow =
    maxSlidesToShow >= CAROUSEL_SLIDES_TO_SHOW
      ? CAROUSEL_SLIDES_TO_SHOW
      : maxSlidesToShow;

  const baseSettings: Settings = {
    dots: false,
    speed: 200,
    touchThreshold: 10,
    swipeToSlide: true,
    focusOnSelect: true,
    arrows: false,
  };

  const settings: Settings = {
    ...baseSettings,
    slidesToShow,
    className: 'slider-thumbnail',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow,
          infinite: true,
        },
      },
      {
        breakpoint: 280,
        settings: {
          slidesToShow: 0,
          initialSlide: 0,
        },
      },
    ],
  };

  const mainSettings: Settings = {
    ...baseSettings,
    slidesToShow: 1,
    adaptiveHeight: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          arrows: true,
          nextArrow: (
            <ChevronIcon
              classes="mr-4 slick-arrow slick-next h-4 w-4"
              direction="right"
            />
          ),
          prevArrow: (
            <ChevronIcon
              classes="ml-4 slick-arrow slick-prev h-4 w-4"
              direction="left"
            />
          ),
          dots: true,
        },
      },
    ],
  };

  return (
    <div
      data-testid={id}
      className="luv-carousel product-slider mx-auto max-w-[1020px]"
    >
      <div className="grid grid-cols-1">
        <Slider
          {...mainSettings}
          ref={mainSliderRef}
          asNavFor={thumbnailSlider as SLIDER_TYPE}
          className={classes}
        >
          {components.map((content) => {
            return (
              <MediaSlide
                hasControls={true}
                {...content}
                key={content?.sys?.id}
              />
            );
          })}
        </Slider>
        <div className="relative mx-auto hidden w-[80%] max-w-[820px] px-2 lg:block">
          <Slider
            {...settings}
            ref={thumbnailSliderRef}
            asNavFor={mainSlider as SLIDER_TYPE}
          >
            {components.map((content) => {
              return (
                <MediaSlide
                  hasControls={false}
                  {...content}
                  key={content?.sys?.id}
                  isThumbnail
                />
              );
            })}
          </Slider>
          <ThumbnailArrow direction="left" onClick={previous} />
          <ThumbnailArrow direction="right" onClick={next} />
        </div>
      </div>
    </div>
  );
};

export default memo(CarouselProducts);
